import * as React from 'react';
import { observer, useLocalStore } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { appConfigStore } from 'Models/AppConfigStore';
import { store } from 'Models/Store';
import {
	Bookings,
	FetchBookingsForCustomerBookingsList,
	FetchMyBookingKinds,
} from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { ParkingReservationDto } from 'Services/Api/_HumanWritten/ParkingService';
import { exportMyBookings } from 'Services/Api/_HumanWritten/ReportService';
import { getMessageByKey, getPascalCaseMessageByKey } from 'Stores/LanguageStore';
import { BookingListSummaryDto } from 'Util/_HumanWritten/Bookings/FetchBookingsWithFilters';
import type { bookingType } from 'Models/Enums';
import { getCustomerExportColumns } from 'Views/Components/_HumanWritten/StaffTables/StaffBookings/BookingColumnKeys';
import { UserInvoiceListComponent } from 'Views/Components/_HumanWritten/UserInvoiceList/UserInvoiceListComponent';
import Tabs from 'Views/Components/Tabs/Tabs';
import { Button } from 'Modules/Ui';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { charterQueryKeys } from 'Modules/Charter/Services/charterQueryKeys';
import { fetchMyCharters } from 'Modules/Charter/Services/CustomerCharterApi';
import { CustomerCharter, customerCharterIsPast, customerCharterName } from 'Modules/Charter/Types/CustomerCharter';
import { bookingListStore } from './BookingListStore';
import { BookingListTab } from './Tabs/BookingListTab';
import { CharterBookingCard } from './CharterBookingCard';
import { ParkingBookingCard } from './ParkingBookingCard';

interface BookingFilterOption {
	key: string;
	label: string;
}

// The chip keys that name an actual ferry booking type map one-for-one onto the bookingTypes the
// export endpoint filters on. PARKING and CHARTER are not booking types — they are separate
// entities that the card list merges in — so neither appears here, and the export button hides
// itself while one of those chips is selected.
const EXPORTABLE_BOOKING_TYPES: Record<string, bookingType> = {
	VEHICLE: 'VEHICLE',
	PASSENGER_ONLY: 'PASSENGER_ONLY',
	FREIGHT: 'FREIGHT',
	EVENT_BOOKING: 'EVENT_BOOKING',
};

// Parking's export column keys, as ParkingExportReportService names them. Staff get the customer's
// name, email and phone and the transaction id as well; on a customer's own file the first three are
// their own details repeated on every row and the last is not theirs to read.
const CUSTOMER_PARKING_EXPORT_COLUMNS = [
	'humanReadableId',
	'createdDate',
	'location',
	'registration',
	'startDate',
	'endDate',
	'totalPrice',
	'status',
];

/** "a", "a and b", "a, b and c" — read aloud rather than comma-separated to the end. */
const formatSectionList = (parts: string[]): string => {
	if (parts.length <= 1) {
		return parts[0] ?? '';
	}
	return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

/**
 * The chips this customer is offered.
 *
 * Events, charters and parking are the three a customer can plausibly never have touched, so what
 * they have outranks what app config says — in both directions. A chip that can only ever produce an
 * empty list is worse than no chip, and a customer who booked parking before the client switched it
 * off still has those reservations and still needs to find them.
 *
 * So events and charters follow what the customer has, full stop; parking shows when the client
 * sells it *or* the customer already has some. The remaining kinds are left on config alone: they
 * are the ordinary way to travel, and someone making their first booking should still see them.
 */
const getBookingTypeOptions = (
	hasEvents: boolean,
	hasCharters: boolean,
	hasParking: boolean,
): BookingFilterOption[] => {
	const options: BookingFilterOption[] = [{ key: 'ALL', label: 'All types' }];

	if (appConfigStore.vehicleEnabled) {
		options.push({
			key: 'VEHICLE',
			label: `${getPascalCaseMessageByKey('vehicle__name__label', 'Vehicle')}`,
		});
	}

	if (appConfigStore.passengerOnlyBookingEnabled) {
		options.push({
			key: 'PASSENGER_ONLY',
			label: `${getPascalCaseMessageByKey('passenger__name__label', 'Walk-on')}`,
		});
	}

	if (appConfigStore.freightEnabled) {
		options.push({
			key: 'FREIGHT',
			label: getMessageByKey('freight__name__label') || 'Freight',
		});
	}

	if (hasEvents) {
		options.push({ key: 'EVENT_BOOKING', label: 'Event' });
	}

	if (appConfigStore.parkingEnabled || hasParking) {
		options.push({ key: 'PARKING', label: 'Parking' });
	}

	if (hasCharters) {
		options.push({ key: 'CHARTER', label: 'Charter' });
	}

	return options;
};

function BookingCardList() {
	const {
		upcomingBookings,
		setUpcomingBookings,
		addToUpcomingBookings,
		pastBookings,
		setPastBookings,
		addToPastBookings,
		setTotalCountOfPastBookings,
		setTotalCountOfUpcomingBookings,
		totalCountOfPastBookings,
		totalCountOfUpcomingBookings,
	} = bookingListStore();

	// Which kinds of booking this customer actually has — one cheap EXISTS each, not the list itself,
	// so it can be asked once and kept. Feeds both the chips on offer and the summary of what the
	// export will contain, so it runs whatever the app config says.
	const { data: bookingKinds } = useQuery({
		queryKey: ['my-booking-kinds', store.userId],
		queryFn: FetchMyBookingKinds,
	});

	const [bookingType, setBookingType] = useState('ALL');
	const [searchQuery, setSearchQuery] = useState('');
	const [activeTab, setActiveTab] = useState('upcoming-trips');
	const [showSeeMoreButtonOnUpcomingTab, setShowSeeMoreButtonOnUpcomingTab] = useState(true);
	const [showSeeMoreButtonOnPastTab, setShowSeeMoreButtonOnPastTab] = useState(true);
	const [loading, setLoading] = useState(false);
	const [exporting, setExporting] = useState(false);

	const [upcomingParking, setUpcomingParking] = useState<ParkingReservationDto[]>([]);
	const [pastParking, setPastParking] = useState<ParkingReservationDto[]>([]);
	const [upcomingParkingCount, setUpcomingParkingCount] = useState(0);
	const [pastParkingCount, setPastParkingCount] = useState(0);

	// The customer's own charters, all of them in one read — a customer has a handful at most, so
	// there is nothing to page through and nothing to refetch as the tabs are used.
	// Fetched whatever app config says: a customer who has charters keeps them after the client stops
	// selling them, and the chip is offered on what they have rather than what is on sale.
	const { data: charters } = useQuery({
		queryKey: charterQueryKeys.mine,
		queryFn: fetchMyCharters,
	});

	const hasFerryBookings = bookingKinds?.hasFerryBookings === true;
	const hasEventBookings = bookingKinds?.hasEventBookings === true;
	const hasParkingReservations = bookingKinds?.hasParkingReservations === true;
	const hasCharters = (charters?.length ?? 0) > 0;

	const bookingTypeOptions = useMemo(
		() => getBookingTypeOptions(hasEventBookings, hasCharters, hasParkingReservations),
		[hasEventBookings, hasCharters, hasParkingReservations],
	);

	// A chip can disappear under the customer while it is selected — the charter list arriving after
	// first paint is the ordinary case. Fall back to All types rather than leaving the list filtered
	// by something with no chip left to unpick it.
	useEffect(() => {
		if (bookingType !== 'ALL' && !bookingTypeOptions.some(opt => opt.key === bookingType)) {
			setBookingType('ALL');
		}
	}, [bookingTypeOptions, bookingType]);

	useEffect(() => {
		fetchSomeBookings('FUTURE', bookingType, true);
		fetchSomeBookings('PAST', bookingType, true);
	}, [bookingType]);

	useEffect(() => {
		const hasMoreBookings = totalCountOfPastBookings > (pastBookings?.length ?? 0);
		const hasMoreParking = pastParkingCount > pastParking.length;
		setShowSeeMoreButtonOnPastTab(hasMoreBookings || hasMoreParking);

		const hasMoreUpcomingBookings = totalCountOfUpcomingBookings > (upcomingBookings?.length ?? 0);
		const hasMoreUpcomingParking = upcomingParkingCount > upcomingParking.length;
		setShowSeeMoreButtonOnUpcomingTab(hasMoreUpcomingBookings || hasMoreUpcomingParking);
	}, [
		totalCountOfPastBookings,
		pastBookings,
		pastParking,
		pastParkingCount,
		totalCountOfUpcomingBookings,
		upcomingBookings,
		upcomingParking,
		upcomingParkingCount,
	]);

	// Track shared skip counter per direction
	const [upcomingSkip, setUpcomingSkip] = useState(0);
	const [pastSkip, setPastSkip] = useState(0);

	const fetchSomeBookings = async (mode: 'PAST' | 'FUTURE', type: string, refreshList = false) => {
		setLoading(true);

		const upcoming = mode === 'FUTURE';
		let currentSkip = 0;
		if (!refreshList) {
			currentSkip = upcoming ? upcomingSkip : pastSkip;
		}

		const newBookings = await FetchBookingsForCustomerBookingsList(store.userId ?? '', upcoming, currentSkip, type);

		const nextSkip = currentSkip + 5;

		if (upcoming) {
			if (refreshList) {
				setUpcomingBookings(newBookings.bookings);
				setUpcomingParking(newBookings.parkingReservations);
				setUpcomingSkip(5);
			} else {
				addToUpcomingBookings(newBookings.bookings);
				setUpcomingParking(prev => [...prev, ...newBookings.parkingReservations]);
				setUpcomingSkip(nextSkip);
			}
			setTotalCountOfUpcomingBookings(newBookings.totalBookingCount);
			setUpcomingParkingCount(newBookings.totalParkingCount);
		} else {
			if (refreshList) {
				setPastBookings(newBookings.bookings);
				setPastParking(newBookings.parkingReservations);
				setPastSkip(5);
			} else {
				addToPastBookings(newBookings.bookings);
				setPastParking(prev => [...prev, ...newBookings.parkingReservations]);
				setPastSkip(nextSkip);
			}
			setTotalCountOfPastBookings(newBookings.totalBookingCount);
			setPastParkingCount(newBookings.totalParkingCount);
		}

		setLoading(false);
	};

	// Client-side search filter
	const filterBySearch = (bookings: BookingListSummaryDto[] | null, parking: ParkingReservationDto[]) => {
		const q = searchQuery.trim().toLowerCase();
		if (!q) {
			return { bookings, parking };
		}

		const filteredBookings =
			bookings?.filter(b => {
				const searchFields = [
					b.humanReadableId,
					b.name,
					b.email,
					b.passengerDetailsString,
					b.cargoDriverDetailsString,
					b.cargoRegoAndTrailerDetailsString,
					b.eventName,
					b.freightDetailsString,
					b.route?.departure?.name,
					b.route?.destination?.name,
				]
					.filter(Boolean)
					.join(' ')
					.toLowerCase();
				return searchFields.includes(q);
			}) ?? null;

		const filteredParking = parking.filter(r => {
			const searchFields = [r.humanReadableId, r.parkingLocation?.name, r.vehicles?.[0]?.registration]
				.filter(Boolean)
				.join(' ')
				.toLowerCase();
			return searchFields.includes(q);
		});

		return { bookings: filteredBookings, parking: filteredParking };
	};

	const isParkingFilter = bookingType === 'PARKING';
	const isCharterFilter = bookingType === 'CHARTER';

	// Charters show under All types and under Charter, and nowhere else — every other chip names a
	// kind of booking, and a charter is not one.
	const showCharters = hasCharters && (bookingType === 'ALL' || isCharterFilter);

	// Searched over the three things a customer would type: the charter's reference, the invoice
	// number they were quoted, and what the charter is called.
	const filterChartersBySearch = (list: CustomerCharter[]) => {
		const q = searchQuery.trim().toLowerCase();
		if (!q) {
			return list;
		}
		return list.filter(charter =>
			[charter.humanReadableId, charter.invoiceNumber, customerCharterName(charter)]
				.filter(Boolean)
				.join(' ')
				.toLowerCase()
				.includes(q),
		);
	};

	// Split on the end rather than the start, so a charter that is out on the water right now is
	// still upcoming — and on the status ahead of either, so one that was called off is Past whatever
	// its dates say. Past reads newest first, the way the past bookings beside it do.
	const upcomingCharters = useMemo(() => {
		if (!showCharters) {
			return [];
		}
		return filterChartersBySearch((charters ?? []).filter(charter => !customerCharterIsPast(charter)));
	}, [charters, showCharters, searchQuery]);

	const pastCharters = useMemo(() => {
		if (!showCharters) {
			return [];
		}
		return filterChartersBySearch((charters ?? []).filter(customerCharterIsPast)).reverse();
	}, [charters, showCharters, searchQuery]);

	// What the tab numbers count, before the search box narrows anything — the booking counts beside
	// them are server totals and behave the same way.
	const upcomingCharterCount = showCharters
		? (charters ?? []).filter(charter => !customerCharterIsPast(charter)).length
		: 0;
	const pastCharterCount = showCharters ? (charters ?? []).filter(customerCharterIsPast).length : 0;

	// Narrowed to charters, nothing else in the list counts. The bookings and parking already loaded
	// are held back rather than refetched, and the "See more" button goes with them — every charter
	// is already here, so another page of bookings would add nothing to the list.
	const upcomingTabCount =
		(isCharterFilter ? 0 : totalCountOfUpcomingBookings + upcomingParkingCount) + upcomingCharterCount;
	const pastTabCount = (isCharterFilter ? 0 : totalCountOfPastBookings + pastParkingCount) + pastCharterCount;

	const upcomingFiltered = useMemo(
		() =>
			filterBySearch(
				isParkingFilter || isCharterFilter ? [] : upcomingBookings,
				isCharterFilter ? [] : upcomingParking,
			),
		[upcomingBookings, upcomingParking, searchQuery, isParkingFilter, isCharterFilter],
	);

	const pastFiltered = useMemo(
		() => filterBySearch(isParkingFilter || isCharterFilter ? [] : pastBookings, isCharterFilter ? [] : pastParking),
		[pastBookings, pastParking, searchQuery, isParkingFilter, isCharterFilter],
	);

	// Everything in the list that is not a ferry booking. Handed to the tab as one node, which is
	// also what keeps the tab off its "Nothing to see here" when a customer has only these.
	const renderExtraCards = (reservations: ParkingReservationDto[], charterList: CustomerCharter[]) => {
		if (reservations.length === 0 && charterList.length === 0) {
			return undefined;
		}

		return (
			<>
				{reservations.map(r => (
					<ParkingBookingCard key={`parking-${r.id}`} reservation={r} />
				))}
				{charterList.map(charter => (
					<CharterBookingCard key={`charter-${charter.id}`} charter={charter} />
				))}
			</>
		);
	};

	const showFilters = activeTab !== 'invoices';

	// Export is scoped server-side to the signed-in user: a non-staff caller always gets their own
	// bookings and the endpoint ignores whatever userId they send. The userId below is for the staff
	// case — this page is reachable by staff too, and for them the server honours it, so without it
	// their export would widen to every booking in the system rather than the ones on this page.
	//
	// The file is the customer's whole booking history, upcoming and past together, narrowed only by
	// the type chip. The Upcoming / Past tabs are a way of reading the list, not a filter a customer
	// would expect a download to inherit — someone exporting their bookings for their records wants
	// the lot, and otherwise has to do it twice and join the files.
	//
	// The search box is deliberately not passed on either: it filters the loaded cards over name,
	// rego, route and passenger details, while the server's booking search only matches booking ids,
	// so sending it would export a narrower set than the one on screen.
	//
	// No count is shown on the button. The totals on this page come from the customer bookings
	// endpoint, which draws its upcoming / past boundary and its type filters differently from the
	// export's query (whole-booking vs latest-alteration, for one), and the tab numbers beside them
	// also take in parking and charters, which are separate entities and not in this file. Any
	// number here would therefore disagree with the row count for some customers.
	// The chip scopes the file the same way it scopes the list. A chip naming a booking type leaves
	// parking out, because no reservation is one; the Parking chip leaves the bookings out for the
	// same reason. Charters are not exportable yet, so that chip still hides the button.
	const selectedExportType = EXPORTABLE_BOOKING_TYPES[bookingType];
	const includeBookings = !isParkingFilter;
	const includeParking = !selectedExportType && !isCharterFilter;

	const showExport = activeTab !== 'invoices' && !isCharterFilter;

	// The sections the file will actually hold, named one by one. Said in words rather than a count:
	// the page's own totals come from a different query than the export runs, so a number here would
	// drift for some customers, while which kinds are present is something both agree on.
	//
	// "Ferry bookings" rather than bare "bookings" wherever no chip has already named a type —
	// standing beside parking reservations, "bookings" alone reads as though it covers those too.
	const exportSections = useMemo(() => {
		const sections: string[] = [];
		const bookingCount = totalCountOfUpcomingBookings + totalCountOfPastBookings;

		if (includeBookings) {
			if (selectedExportType) {
				// A chip has already named the kind, and the count behind it is that chip's own.
				if (bookingCount > 0) {
					const chipLabel = bookingTypeOptions.find(opt => opt.key === bookingType)?.label ?? '';
					sections.push(`${chipLabel.toLowerCase()} bookings`);
				}
			} else {
				if (hasFerryBookings) {
					sections.push('ferry bookings');
				}
				if (hasEventBookings) {
					sections.push('event bookings');
				}
			}
		}

		// The stable flag rather than the list's parking count: that count is scoped to the chip and
		// drops to zero the moment a booking type is picked, which is exactly when it would be read.
		if (includeParking && hasParkingReservations) {
			sections.push('parking reservations');
		}

		return sections;
	}, [
		includeBookings,
		includeParking,
		selectedExportType,
		bookingType,
		bookingTypeOptions,
		hasFerryBookings,
		hasEventBookings,
		hasParkingReservations,
		totalCountOfUpcomingBookings,
		totalCountOfPastBookings,
	]);

	// The button and the summary read off the same list, so they cannot disagree about whether there
	// is anything to download.
	const hasAnythingToExport = exportSections.length > 0;
	const exportSummary = hasAnythingToExport
		? `Exports all your ${formatSectionList(exportSections)}, upcoming and past`
		: 'Nothing to export';

	const handleExport = async () => {
		setExporting(true);
		try {
			await exportMyBookings({
				filters: {
					// Only "Active" and "Past/Cancelled" narrow the query; anything else leaves the
					// upcoming / past clause off entirely, which is what gets both in one file.
					bookingFilterType: 'All',
					bookingTypes: selectedExportType ? [selectedExportType] : undefined,
					filterByCreatedDate: false,
					statusFilters: [],
					initialFetch: false,
					// A fixed customer column set, gated on the same app config the staff column
					// selector uses — see getCustomerExportColumns for what is in it and what is not.
					columns: getCustomerExportColumns(),
				},
				// Parking's own columns, trimmed of the ones that only mean something to staff: the
				// customer's own name, email and phone on every row, and the transaction id.
				parkingColumns: CUSTOMER_PARKING_EXPORT_COLUMNS,
				includeBookings,
				includeParking,
			});
		} finally {
			setExporting(false);
		}
	};

	return (
		<>
			<div className="bookings-header">
				<h4>Bookings</h4>
				{showExport && (
					<div className="bookings-header__export-group">
						<Button
							variant="outline"
							size="sm"
							type="button"
							data-test-id="customer-bookings-export"
							disabled={exporting || !hasAnythingToExport}
							onClick={handleExport}
						>
							<Icon name="download" useMaterialIcons />
							{exporting ? 'Exporting...' : 'Export'}
						</Button>
						{/* Same shape as the help icons on the CRUD screens (CrudSection): the icon is the
						    trigger, the panel opens on hover, and both sit in the CRUD grey. */}
						<div className="relative group flex items-center gap-1 text-gray-400 cursor-help">
							<span className="material-symbols-outlined text-base">help</span>
							<span className="text-xs">What will be exported?</span>
							<div
								className="absolute right-0 bottom-full mb-2 hidden group-hover:block w-72 px-3 py-2 text-xs text-white bg-gray-800 rounded-md shadow-lg z-10 pointer-events-none"
								data-test-id="customer-bookings-export-hint"
							>
								{exportSummary}
							</div>
						</div>
					</div>
				)}
			</div>
			{showFilters && (
				<div className="bookings-filters">
					<form className="search-box" onSubmit={e => e.preventDefault()}>
						<input
							className="fh-basic-search"
							placeholder="Search by ID, name, rego..."
							autoComplete="off"
							value={searchQuery}
							onChange={e => setSearchQuery(e.target.value)}
						/>
						{searchQuery && (
							<button
								type="button"
								className="bookings-filters__clear"
								onClick={() => setSearchQuery('')}
							>
								<Icon name="close" useMaterialIcons />
							</button>
						)}
					</form>
					<div className="bookings-filters__chips">
						{bookingTypeOptions.map(opt => (
							<button
								key={opt.key}
								type="button"
								data-test-id={`bookings-filter-chip-${opt.key}`}
								className={`bookings-filters__chip ${
									bookingType === opt.key ? 'bookings-filters__chip--active' : ''
								}`}
								onClick={() => setBookingType(opt.key)}
							>
								{opt.label}
							</button>
						))}
					</div>
				</div>
			)}
			<Tabs
				onTabClicked={tabIndex => {
					const tabKeys = ['upcoming-trips', 'past-trips', 'invoices'];
					setActiveTab(tabKeys[tabIndex] || 'upcoming-trips');
				}}
				tabs={[
					{
						component: (
							<BookingListTab
								key="upcoming-trips"
								bookingList={upcomingFiltered.bookings}
								loading={loading}
								showSeeMoreButton={!searchQuery && !isCharterFilter && showSeeMoreButtonOnUpcomingTab}
								onSeeMoreClick={() => fetchSomeBookings('FUTURE', bookingType)}
								extraCards={renderExtraCards(upcomingFiltered.parking, upcomingCharters)}
							/>
						),
						name: (
							<>
								Upcoming <span className="tab-count">{upcomingTabCount}</span>
							</>
						),
						key: 'upcoming-trips',
					},
					{
						component: (
							<BookingListTab
								key="past-trips"
								bookingList={pastFiltered.bookings}
								loading={loading}
								showSeeMoreButton={!searchQuery && !isCharterFilter && showSeeMoreButtonOnPastTab}
								onSeeMoreClick={() => fetchSomeBookings('PAST', bookingType)}
								extraCards={renderExtraCards(pastFiltered.parking, pastCharters)}
							/>
						),
						name: (
							<>
								Past <span className="tab-count">{pastTabCount}</span>
							</>
						),
						key: 'past-trips',
					},
					{
						component: (
							<UserInvoiceListComponent key="invoices" userId={store.userId} bookingType={bookingType} />
						),
						name: 'Invoices',
						key: 'invoices',
					},
				]}
			/>
		</>
	);
}

export default observer(BookingCardList);
